import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import type {
  AddGoalBody,
  AddMeasureBody,
  CarePlanReviewSyncDetail,
  CarePlanViewModel,
  DecideBody,
  DecideBulkBody,
  EditFindingBody,
  EditItemBody,
  FindingResourceType,
  MeasureVm,
  SetScheduleBody,
} from '@eluve/api-contract';
import {
  CARE_PLAN_REVIEW_SYNC_EVENT,
  itemReviewPosture,
  measureReviewPosture,
} from '@eluve/api-contract';
import { toast } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

/** One row of a section-wide bulk confirmation, remembered so Undo can revert
 *  exactly what the bulk action decided. */
export type BulkConfirmRow =
  | { kind: 'finding'; resourceType: FindingResourceType; id: string }
  | {
      kind: 'item';
      itemType: DecideBody['itemType'];
      id: string;
      proposalIds?: string[];
    };

type CarePlanReviewPhase = 'loading' | 'ready' | 'error';
type MutationResult = { status: number };

const FINDING_SECTION: Partial<
  Record<FindingResourceType, DecideBulkBody['section']>
> = { Goal: 'goals', Observation: 'measures' };
const ITEM_SECTION: Record<DecideBody['itemType'], DecideBulkBody['section']> =
  { goal: 'goals', measure: 'measures', 'action-item': 'action-items' };
const sectionsOfFinding = (
  resourceType: FindingResourceType,
): Array<DecideBulkBody['section']> => {
  const section = FINDING_SECTION[resourceType];
  return section ? [section] : [];
};
type WithoutAppointment<T> = T extends { appointmentId: string }
  ? Omit<T, 'appointmentId'>
  : never;

export const useCarePlanReview = (context: {
  tenantId: string;
  appointmentId: string;
}) => {
  const { tenantId, appointmentId } = context;
  const { t } = useTranslation();
  const tsRestClient = useApiClient();
  const [vm, setVm] = useState<CarePlanViewModel | null>(null);
  const [phase, setPhase] = useState<CarePlanReviewPhase>('loading');
  const [mutating, setMutating] = useState(0);
  const contextKey = `${tenantId}:${appointmentId}`;
  const currentContextKey = useRef(contextKey);
  const loadRequestId = useRef(0);
  const mounted = useRef(true);
  currentContextKey.current = contextKey;

  const load = useCallback(async () => {
    if (!mounted.current || currentContextKey.current !== contextKey) return;
    const requestId = ++loadRequestId.current;

    try {
      const result = await tsRestClient.carePlans.getCarePlan({
        params: { tenantId },
        query: { appointmentId },
      });
      if (
        !mounted.current ||
        currentContextKey.current !== contextKey ||
        requestId !== loadRequestId.current
      ) {
        return;
      }
      if (result.status === 200) {
        setVm(result.body);
        setPhase('ready');
      } else {
        setPhase('error');
      }
    } catch {
      if (
        mounted.current &&
        currentContextKey.current === contextKey &&
        requestId === loadRequestId.current
      ) {
        setPhase('error');
      }
    }
  }, [appointmentId, contextKey, tenantId, tsRestClient]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      loadRequestId.current += 1;
    };
  }, []);

  useEffect(() => {
    setPhase('loading');
    void load();
  }, [load]);

  useEffect(() => {
    const syncFromFindings = (event: Event) => {
      const detail = (event as CustomEvent<CarePlanReviewSyncDetail>).detail;
      if (
        detail?.source === 'findings' &&
        detail.tenantId === tenantId &&
        detail.appointmentId === appointmentId
      ) {
        void load();
      }
    };
    window.addEventListener(CARE_PLAN_REVIEW_SYNC_EVENT, syncFromFindings);
    return () =>
      window.removeEventListener(CARE_PLAN_REVIEW_SYNC_EVENT, syncFromFindings);
  }, [appointmentId, load, tenantId]);

  const [bulkConfirmed, setBulkConfirmed] = useState<
    Partial<Record<DecideBulkBody['section'], BulkConfirmRow[]>>
  >({});
  useEffect(() => setBulkConfirmed({}), [contextKey]);

  const write = useCallback(
    async (
      mutation: () => Promise<MutationResult | MutationResult[]>,
      options?: { invalidatesBulk?: Array<DecideBulkBody['section']> },
    ): Promise<boolean> => {
      setMutating((n) => n + 1);
      // Any later verdict or edit in a section makes that section's remembered
      // bulk confirmation stale — Undo-all must never revert subsequent work.
      const staleSections = options?.invalidatesBulk ?? [];
      if (staleSections.length > 0) {
        setBulkConfirmed((prev) => {
          const next = { ...prev };
          for (const section of staleSections) next[section] = undefined;
          return next;
        });
      }
      // A multi-request mutation can partially persist before failing, so the
      // view must be re-read on EVERY outcome, not only success.
      const reloadAndBroadcast = async () => {
        if (currentContextKey.current !== contextKey) return;
        await load();
        window.dispatchEvent(
          new CustomEvent<CarePlanReviewSyncDetail>(
            CARE_PLAN_REVIEW_SYNC_EVENT,
            {
              detail: { tenantId, appointmentId, source: 'care-plan' },
            },
          ),
        );
      };
      try {
        const result = await mutation();
        const results = Array.isArray(result) ? result : [result];
        if (results.some(({ status }) => status !== 200)) {
          toast.error(t('carePlans.review.updateFailed'));
          await reloadAndBroadcast();
          return false;
        }
        if (currentContextKey.current !== contextKey) return false;
        await reloadAndBroadcast();
        return true;
      } catch {
        if (mounted.current && currentContextKey.current === contextKey) {
          toast.error(t('carePlans.review.updateFailed'));
          await reloadAndBroadcast().catch(() => undefined);
        }
        return false;
      } finally {
        if (mounted.current) setMutating((n) => n - 1);
      }
    },
    [appointmentId, contextKey, load, tenantId, t],
  );

  const decideFinding = useCallback(
    (
      resourceType: FindingResourceType,
      id: string,
      decision: 'approve' | 'reject',
    ) =>
      write(
        () =>
          tsRestClient.carePlans.decideFindings({
            params: { tenantId },
            body: {
              appointmentId,
              decisions: [{ resourceType, id, decision }],
            },
          }),
        { invalidatesBulk: sectionsOfFinding(resourceType) },
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const undoFinding = useCallback(
    (resourceType: FindingResourceType, id: string) =>
      write(
        () =>
          tsRestClient.carePlans.undoFindings({
            params: { tenantId },
            body: { appointmentId, targets: [{ resourceType, id }] },
          }),
        { invalidatesBulk: sectionsOfFinding(resourceType) },
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const editFinding = useCallback(
    (
      resourceType: FindingResourceType,
      id: string,
      edit: Omit<WithoutAppointment<EditFindingBody>, 'resourceType' | 'id'>,
    ) =>
      write(
        () =>
          tsRestClient.carePlans.editFinding({
            params: { tenantId },
            body: {
              appointmentId,
              resourceType,
              id,
              display: edit.display,
              targetDate: edit.targetDate,
              done: edit.done,
              isPrimary: edit.isPrimary,
            },
          }),
        { invalidatesBulk: sectionsOfFinding(resourceType) },
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const dismissFinding = useCallback(
    (resourceType: FindingResourceType, id: string) =>
      write(
        () =>
          tsRestClient.carePlans.dismissFinding({
            params: { tenantId },
            body: { appointmentId, resourceType, id },
          }),
        { invalidatesBulk: sectionsOfFinding(resourceType) },
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const decidePlanItem = useCallback(
    (body: WithoutAppointment<DecideBody>) =>
      write(
        () =>
          tsRestClient.carePlans.decide({
            params: { tenantId },
            body: { appointmentId, ...body },
          }),
        { invalidatesBulk: [ITEM_SECTION[body.itemType]] },
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  /** Decides an item's proposals in turn (sequential — each decision rewrites
   *  the same resource). With no proposal ids, decides the item itself once.
   *  The raw mutation runner shared by every grouped-row/bulk path. */
  const decideProposals = useCallback(
    async (args: {
      itemType: DecideBody['itemType'];
      id: string;
      action: 'confirm' | 'reject' | 'undo';
      proposalIds: string[];
    }): Promise<MutationResult[]> => {
      const { itemType, id, action, proposalIds } = args;
      const results: MutationResult[] = [];
      for (const proposalId of proposalIds.length > 0 ? proposalIds : [null]) {
        results.push(
          await tsRestClient.carePlans.decide({
            params: { tenantId },
            body: {
              appointmentId,
              itemType,
              id,
              action,
              ...(proposalId ? { proposalId } : {}),
            },
          }),
        );
      }
      return results;
    },
    [tsRestClient, tenantId, appointmentId],
  );

  /** One verdict for a grouped row: decides every listed proposal together. */
  const decidePlanItemProposals = useCallback(
    (
      body: Omit<WithoutAppointment<DecideBody>, 'proposalId' | 'action'> & {
        action: 'confirm' | 'reject' | 'undo';
        proposalIds: string[];
      },
    ) =>
      write(() => decideProposals(body), {
        invalidatesBulk: [ITEM_SECTION[body.itemType]],
      }),
    [write, decideProposals],
  );

  /** A measure row's verdict covers both of its subjects at once: the
   *  this-visit Observation finding and the AI-proposed target change carried
   *  on its goal. */
  const decideMeasureRow = useCallback(
    (measure: MeasureVm, action: 'approve' | 'reject' | 'undo') =>
      write(
        async () => {
          const mutations: Array<Promise<MutationResult>> = [];
          const capture = measure.thisVisit;
          if (capture && capture.itemState !== 'unchanged') {
            mutations.push(
              action === 'undo'
                ? tsRestClient.carePlans.undoFindings({
                    params: { tenantId },
                    body: {
                      appointmentId,
                      targets: [
                        {
                          resourceType: 'Observation',
                          id: capture.observationId,
                        },
                      ],
                    },
                  })
                : tsRestClient.carePlans.decideFindings({
                    params: { tenantId },
                    body: {
                      appointmentId,
                      decisions: [
                        {
                          resourceType: 'Observation',
                          id: capture.observationId,
                          decision: action,
                        },
                      ],
                    },
                  }),
            );
          }
          const change = measure.targetChange;
          const changeActionable =
            change &&
            (action === 'undo'
              ? change.decision !== 'pending'
              : change.decision === 'pending');
          if (change && changeActionable) {
            mutations.push(
              tsRestClient.carePlans.decide({
                params: { tenantId },
                body: {
                  appointmentId,
                  itemType: 'goal',
                  id: change.goalId,
                  action: action === 'approve' ? 'confirm' : action,
                  proposalId: change.proposalId,
                },
              }),
            );
          }
          return Promise.all(mutations);
        },
        { invalidatesBulk: ['measures'] },
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const confirmAll = useCallback(
    async (section: DecideBulkBody['section'], rows: BulkConfirmRow[]) => {
      const findings = rows.flatMap((row) =>
        row.kind === 'finding' ? [row] : [],
      );
      const proposalRows = rows.flatMap((row) =>
        row.kind === 'item' && row.proposalIds?.length ? [row] : [],
      );
      const ok = await write(async () => {
        const mutations: Array<Promise<MutationResult>> = [];
        if (findings.length > 0) {
          mutations.push(
            tsRestClient.carePlans.decideFindings({
              params: { tenantId },
              body: {
                appointmentId,
                decisions: findings.map(({ resourceType, id }) => ({
                  resourceType,
                  id,
                  decision: 'approve' as const,
                })),
              },
            }),
          );
        }
        if (section !== 'measures') {
          mutations.push(
            tsRestClient.carePlans.decideBulk({
              params: { tenantId },
              body: { appointmentId, section, action: 'confirm' },
            }),
          );
        }
        const results = await Promise.all(mutations);
        // Measure target-change proposals live on goals; decideBulk('measures')
        // doesn't exist server-side, so confirm each proposal directly.
        if (section === 'measures') {
          for (const row of proposalRows) {
            results.push(
              ...(await decideProposals({
                itemType: row.itemType,
                id: row.id,
                action: 'confirm',
                proposalIds: row.proposalIds ?? [],
              })),
            );
          }
        }
        return results;
      });
      if (ok && mounted.current) {
        setBulkConfirmed((prev) => ({ ...prev, [section]: rows }));
      }
    },
    [appointmentId, decideProposals, tsRestClient, tenantId, write],
  );

  /** Reverts the section's last bulk confirmation row by row. */
  const undoConfirmAll = useCallback(
    async (section: DecideBulkBody['section']) => {
      const rows = bulkConfirmed[section] ?? [];
      if (rows.length === 0) return;
      const findings = rows.flatMap((row) =>
        row.kind === 'finding' ? [row] : [],
      );
      const items = rows.flatMap((row) => (row.kind === 'item' ? [row] : []));
      const ok = await write(async () => {
        const results: MutationResult[] = [];
        if (findings.length > 0) {
          results.push(
            await tsRestClient.carePlans.undoFindings({
              params: { tenantId },
              body: {
                appointmentId,
                targets: findings.map(({ resourceType, id }) => ({
                  resourceType,
                  id,
                })),
              },
            }),
          );
        }
        for (const item of items) {
          results.push(
            ...(await decideProposals({
              itemType: item.itemType,
              id: item.id,
              action: 'undo',
              proposalIds: item.proposalIds ?? [],
            })),
          );
        }
        return results;
      });
      if (ok && mounted.current) {
        setBulkConfirmed((prev) => ({ ...prev, [section]: undefined }));
      }
    },
    [
      appointmentId,
      bulkConfirmed,
      decideProposals,
      tsRestClient,
      tenantId,
      write,
    ],
  );

  const editPlanItem = useCallback(
    (body: WithoutAppointment<EditItemBody>) =>
      write(
        () =>
          tsRestClient.carePlans.edit({
            params: { tenantId },
            body: { appointmentId, ...body },
          }),
        { invalidatesBulk: [ITEM_SECTION[body.itemType]] },
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const setPlanPhase = useCallback(
    (phaseNumber: number) =>
      write(() =>
        tsRestClient.carePlans.setPhase({
          params: { tenantId },
          body: { appointmentId, phase: phaseNumber },
        }),
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const setSchedule = useCallback(
    (args: WithoutAppointment<SetScheduleBody>) =>
      write(() =>
        tsRestClient.carePlans.setSchedule({
          params: { tenantId },
          body: { appointmentId, ...args },
        }),
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const addGoal = useCallback(
    (args: WithoutAppointment<AddGoalBody>) =>
      write(
        () =>
          tsRestClient.carePlans.addGoal({
            params: { tenantId },
            body: { appointmentId, ...args },
          }),
        { invalidatesBulk: ['goals'] },
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const addMeasure = useCallback(
    (args: WithoutAppointment<AddMeasureBody>) =>
      write(
        () =>
          tsRestClient.carePlans.addMeasure({
            params: { tenantId },
            body: { appointmentId, ...args },
          }),
        { invalidatesBulk: ['measures'] },
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const addActionItem = useCallback(
    (text: string) =>
      write(
        () =>
          tsRestClient.carePlans.addActionItem({
            params: { tenantId },
            body: { appointmentId, text },
          }),
        { invalidatesBulk: ['action-items'] },
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const activate = useCallback(
    () =>
      write(() =>
        tsRestClient.carePlans.activate({
          params: { tenantId },
          body: { appointmentId },
        }),
      ),
    [write, tsRestClient, tenantId, appointmentId],
  );

  const reviewTally = useMemo(() => {
    if (!vm) return { total: 0, reviewed: 0, approved: 0, rejected: 0 };
    const decisions = [
      ...vm.goals.map(itemReviewPosture),
      ...vm.measures.map(measureReviewPosture),
      ...vm.actionItems.map(itemReviewPosture),
    ]
      .filter((posture) => posture.reviewable)
      .map((posture) => posture.decision);
    const reviewed = decisions.filter((d) => d !== 'pending').length;
    return {
      total: decisions.length,
      reviewed,
      approved: decisions.filter((d) => d === 'approved' || d === 'edited')
        .length,
      rejected: decisions.filter((d) => d === 'rejected').length,
    };
  }, [vm]);

  return {
    phase,
    vm,
    mutating: mutating > 0,
    reviewTally,
    bulkConfirmed,
    decideFinding,
    undoFinding,
    editFinding,
    dismissFinding,
    decidePlanItem,
    decidePlanItemProposals,
    decideMeasureRow,
    confirmAll,
    undoConfirmAll,
    editPlanItem,
    setPlanPhase,
    setSchedule,
    addGoal,
    addMeasure,
    addActionItem,
    activate,
  };
};
